import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ClipboardCheck } from 'lucide-react';
import { BAD_STATUSES } from './types';

interface StatusConfirmationProps {
  statusType: string;
  newStatus: string;
  taskCreated: boolean;
  onDismiss: () => void;
}

export function StatusConfirmation({ statusType, newStatus, taskCreated, onDismiss }: StatusConfirmationProps) {
  const { t } = useTranslation('visitor');
  const isBadStatus = BAD_STATUSES.has(newStatus);

  // Haptic feedback on status update confirmation
  useEffect(() => {
    navigator.vibrate?.(50);
  }, []);

  // Close on Escape, the dialog advertises modal semantics; AT users expect
  // Escape to dismiss it.
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onDismiss]);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmation-title"
      onClick={onDismiss}
    >
      <div
        className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl"
        style={{ animation: 'scaleIn 0.2s ease-out' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
          style={{
            background: isBadStatus
              ? 'linear-gradient(135deg, #40916C 0%, #52B788 100%)'
              : 'linear-gradient(135deg, #52B788 0%, #74C69D 100%)',
          }}
        >
          {isBadStatus ? (
            <ClipboardCheck className="w-10 h-10 text-white" strokeWidth={2} />
          ) : (
            <Check className="w-10 h-10 text-white" strokeWidth={3} />
          )}
        </div>

        {/* Title */}
        <h2 id="confirmation-title" className="text-2xl font-bold mb-2" style={{ color: '#1B4332' }}>
          {isBadStatus ? t('statusConfirmation.titleBadStatus') : t('statusConfirmation.titleGoodStatus')}
        </h2>

        {/* Description */}
        <p className="text-muted-foreground mb-6 leading-relaxed">
          {isBadStatus
            ? t('statusConfirmation.messageBadStatus', { type: statusType.toLowerCase() })
            : t('statusConfirmation.messageGoodStatus')}
        </p>

        {/* Task created notice */}
        {isBadStatus && taskCreated && (
          <div
            className="text-[15px] p-4 rounded-2xl mb-6 flex items-start gap-3 text-start"
            style={{ backgroundColor: 'rgba(45, 106, 79, 0.08)' }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: '#2D6A4F' }}
            >
              <ClipboardCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-semibold text-foreground mb-0.5">{t('statusConfirmation.serviceRequestCreated')}</p>
              <p className="text-muted-foreground text-[15px]">{t('statusConfirmation.serviceRequestMessage')}</p>
            </div>
          </div>
        )}

        {/* Done button */}
        <button
          onClick={onDismiss}
          className="w-full h-12 rounded-xl text-white font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
          style={{ backgroundColor: '#2D6A4F' }}
        >
          {t('common:buttons.done')}
        </button>
      </div>
    </div>
  );
}
